import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Baby,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Gem,
  Gift,
  Church,
  Palette,
  Camera,
  Crown,
  Flower2,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// Product categories for baptism
const PRODUCT_CATEGORIES = [
  { value: 'bomboniere', label: 'Bomboniere', icon: Gift },
  { value: 'inviti', label: 'Inviti e partecipazioni', icon: MessageSquare },
  { value: 'confettata', label: 'Confettata / Sweet table', icon: Gift },
  { value: 'centrotavola', label: 'Centrotavola', icon: Flower2 },
  { value: 'decorazioni', label: 'Decorazioni e allestimenti', icon: Sparkles },
  { value: 'album', label: 'Album e libri firma', icon: Camera },
  { value: 'abbigliamento', label: 'Vestitino / Accessori bimbo', icon: Baby },
  { value: 'tableau', label: 'Tableau e segnaposto', icon: Palette },
  { value: 'torta', label: 'Torta personalizzata', icon: Star },
  { value: 'candele', label: 'Candele battesimali', icon: Sparkles },
  { value: 'pacchetto', label: 'Pacchetto completo', icon: Crown },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Themes
const THEMES = [
  { value: 'angeli', label: 'Angeli e nuvole', icon: Sparkles },
  { value: 'orsetto', label: 'Orsetto / Peluche', icon: Heart },
  { value: 'fiori', label: 'Fiori e farfalle', icon: Flower2 },
  { value: 'marino', label: 'Marino / Ancora', icon: Star },
  { value: 'stelle', label: 'Stelle e luna', icon: Star },
  { value: 'mongolfiera', label: 'Mongolfiera', icon: Sparkles },
  { value: 'safari', label: 'Safari / Animaletti', icon: Heart },
  { value: 'principessa', label: 'Principessa / Fatina', icon: Crown },
  { value: 'principe', label: 'Principe / Cavaliere', icon: Crown },
  { value: 'elegante', label: 'Elegante / Classico', icon: Gem },
  { value: 'rustico', label: 'Rustico / Country', icon: Heart },
  { value: 'boho', label: 'Boho / Bohemien', icon: Sparkles },
  { value: 'personalizzato', label: 'Tema personalizzato', icon: Palette },
];

// Colors
const COLOR_SCHEMES = [
  { value: 'bianco_rosa', label: 'Bianco e rosa', icon: Heart },
  { value: 'bianco_azzurro', label: 'Bianco e azzurro', icon: Star },
  { value: 'bianco_oro', label: 'Bianco e oro', icon: Crown },
  { value: 'bianco_argento', label: 'Bianco e argento', icon: Gem },
  { value: 'rosa_oro', label: 'Rosa e oro', icon: Sparkles },
  { value: 'azzurro_argento', label: 'Azzurro e argento', icon: Star },
  { value: 'panna_verde', label: 'Panna e verde salvia', icon: Flower2 },
  { value: 'pastello', label: 'Colori pastello misti', icon: Sparkles },
  { value: 'naturale', label: 'Toni naturali', icon: Heart },
  { value: 'personalizzato', label: 'Colori personalizzati', icon: Palette },
];

// Guest counts
const GUEST_COUNTS = [
  { value: '10-25', label: '10-25 invitati', description: 'Cerimonia intima' },
  { value: '25-50', label: '25-50 invitati', description: 'Cerimonia media' },
  { value: '50-80', label: '50-80 invitati', description: 'Cerimonia grande' },
  { value: '80-120', label: '80-120 invitati', description: 'Grande evento' },
  { value: '120+', label: '120+ invitati', description: 'Evento molto grande' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '100-250', label: '€100 - €250', description: 'Bomboniere base' },
  { value: '250-500', label: '€250 - €500', description: 'Allestimento semplice' },
  { value: '500-800', label: '€500 - €800', description: 'Pacchetto curato' },
  { value: '800-1200', label: '€800 - €1200', description: 'Evento elaborato' },
  { value: '1200-2000', label: '€1200 - €2000', description: 'Premium' },
  { value: '2000+', label: '€2000+', description: 'Esclusivo' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '1month', label: '1 Mese', description: 'Prioritario' },
  { value: '2months', label: '2 Mesi', description: 'Standard' },
  { value: '3months', label: '3+ Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Data non definita' },
];

// Examples
const BAPTISM_EXAMPLES = [
  {
    idea: "Bomboniere per battesimo bimba, tema angeli",
    result: "Angioletti in ceramica dipinti a mano, confezionati con tulle rosa e confetti di Sulmona",
    image: "https://images.pexels.com/photos/35060140/pexels-photo-35060140.jpeg?w=400&h=300&fit=crop",
    category: "Bomboniere"
  },
  {
    idea: "Inviti eleganti per battesimo con calligrafia",
    result: "Partecipazioni in carta cotone con calligrafia a mano, sigillo in ceralacca e busta foderata",
    image: "https://images.pexels.com/photos/11503476/pexels-photo-11503476.jpeg?w=400&h=300&fit=crop",
    category: "Inviti"
  },
  {
    idea: "Allestimento completo tema orsetto",
    result: "Centrotavola con orsetti in feltro, tableau personalizzato, confettata coordinata, candela battesimale",
    image: "https://images.pexels.com/photos/16537217/pexels-photo-16537217.png?w=400&h=300&fit=crop",
    category: "Pacchetto"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Le bomboniere per il battesimo di Sofia erano perfette! Angioletti in ceramica dipinti a mano, ognuno leggermente diverso. Gli invitati sono rimasti incantati dalla cura dei dettagli.",
    author: "Valentina M.",
    location: "Milano"
  },
  {
    text: "Cercavamo qualcosa di unico per il battesimo di nostro figlio. L'artigiana ha creato degli inviti con la nostra storia illustrata. Un ricordo che conserveremo per sempre.",
    author: "Marco e Chiara",
    location: "Roma"
  },
  {
    text: "Pacchetto completo per il battesimo: dalle bomboniere al tableau, tutto coordinato perfettamente. Professionalità e creatività al top. Consigliatissimo!",
    author: "Francesca L.",
    location: "Firenze"
  }
];

// FAQ
const FAQ_ITEMS = [
  {
    question: "Con quanto anticipo devo ordinare le bomboniere?",
    answer: "Per bomboniere artigianali personalizzate, consigliamo almeno 6-8 settimane di anticipo. Per allestimenti completi, meglio 2-3 mesi. In caso di urgenza, alcuni artigiani offrono servizi express."
  },
  {
    question: "Posso avere un campione prima di ordinare?",
    answer: "Certamente! La maggior parte degli artigiani offre campioni a pagamento (spesso rimborsato con l'ordine). È il modo migliore per verificare qualità e colori dal vivo."
  },
  {
    question: "Che tipo di bomboniere vanno per un battesimo?",
    answer: "Le più apprezzate sono: ceramiche dipinte (angeli, animaletti), sacchettini ricamati, oggetti in legno inciso, candele profumate, vasetti con piantine. L'artigiano ti consiglierà in base al tema scelto."
  },
  {
    question: "Posso personalizzare con nome e data?",
    answer: "Assolutamente sì! Quasi tutti i prodotti possono essere personalizzati con nome del bimbo/a, data del battesimo e, se vuoi, una frase o dedica speciale."
  },
  {
    question: "Offrite anche i confetti?",
    answer: "Molti artigiani collaborano con confetterie artigianali e possono includere confetti di alta qualità (Sulmona, cioccolato belga, gusti speciali) nella bomboniera."
  }
];

export default function BattesimiRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    child_name: '',
    child_gender: '',
    product_category: '',
    theme: '',
    color_scheme: '',
    guest_count: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    setUploadingFile(true);
    for (const file of files) {
      if (!file.type.startsWith('image/')) { toast.error('Solo immagini'); continue; }
      if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); continue; }
      try {
        const fileName = `baptism-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const { error } = await supabase.storage.from('rarifex-uploads').upload(`requests/${fileName}`, file);
        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage.from('rarifex-uploads').getPublicUrl(`requests/${fileName}`);
        setFormData(prev => ({ ...prev, attachments: [...prev.attachments, publicUrl] }));
      } catch (error) { toast.error('Errore upload'); }
    }
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) { toast.error('Descrivi cosa cerchi per il battesimo'); return; }
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_baptism_request', JSON.stringify(formData));
      return;
    }
    setLoading(true);
    try {
      const productLabel = PRODUCT_CATEGORIES.find(t => t.value === formData.product_category)?.label || '';
      const themeLabel = THEMES.find(t => t.value === formData.theme)?.label || '';
      const colorLabel = COLOR_SCHEMES.find(t => t.value === formData.color_scheme)?.label || '';
      const guestLabel = GUEST_COUNTS.find(t => t.value === formData.guest_count)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      const details = [];
      if (formData.child_name) details.push(`👶 Nome: ${formData.child_name}`);
      if (formData.child_gender) details.push(`${formData.child_gender === 'M' ? '👦' : '👧'} Sesso: ${formData.child_gender === 'M' ? 'Maschietto' : 'Femminuccia'}`);
      if (productLabel) details.push(`🎁 Cerco: ${productLabel}`);
      if (themeLabel) details.push(`✨ Tema: ${themeLabel}`);
      if (colorLabel) details.push(`🎨 Colori: ${colorLabel}`);
      if (guestLabel) details.push(`👥 Invitati: ${guestLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        fullDescription += `\n\n━━━━━━━━━━━━━━━━━━━━━━━\n🕊️ DETTAGLI BATTESIMO\n━━━━━━━━━━━━━━━━━━━━━━━\n` + details.join('\n');
      }
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: user.id,
          title: formData.title || `Battesimo ${formData.child_name || ''} - ${productLabel}`.trim(),
          description: fullDescription,
          category: 'Battesimi',
          price_range: formData.budget || 'flexible',
          timeline: formData.timeline || 'flexible',
          attachments: formData.attachments,
        }),
      });
      if (!response.ok) throw new Error('Errore');
      toast.success('Richiesta inviata! Riceverai presto proposte.');
      navigate('/requests');
    } catch (error) { toast.error('Errore nell\'invio'); }
    finally { setLoading(false); }
  };

  const scrollToForm = () => document.getElementById('baptism-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <>
      <Helmet>
        <title>Battesimo Su Misura | Bomboniere e Inviti Artigianali | Rarifex</title>
        <meta name="description" content="Organizza il battesimo perfetto con bomboniere e inviti artigianali italiani. Ceramiche dipinte a mano, partecipazioni calligrafiche, allestimenti personalizzati." />
        <meta name="keywords" content="battesimo, bomboniere battesimo, inviti battesimo, bomboniere artigianali, partecipazioni battesimo, allestimento battesimo" />
        <link rel="canonical" href="https://www.rarifex.com/battesimi-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-white to-blue-50/30">
        
        {/* HERO */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-100/50 via-transparent to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Baby className="w-4 h-4" />
                    Battesimo Artigianale
                  </div>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Un battesimo{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-blue-600">
                      indimenticabile
                    </span>
                  </h1>
                  <p className="text-xl text-slate-600 mb-8">
                    Bomboniere, inviti e decorazioni artigianali per celebrare il giorno più speciale.
                  </p>
                  <p className="text-lg text-slate-500 mb-8">
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /><span>Artigiani verificati</span></div>
                    <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-sky-500" /><span>Risposte in 24-48h</span></div>
                    <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-slate-500" /><span>100% Made in Italy</span></div>
                  </div>
                </div>
                
                <div id="baptism-form">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Nome bimbo/a</Label>
                          <Input placeholder="Es: Sofia" value={formData.child_name} onChange={(e) => handleInputChange('child_name', e.target.value)} className="mt-1.5" data-testid="baptism-child-name" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Sesso</Label>
                          <select value={formData.child_gender} onChange={(e) => handleInputChange('child_gender', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="baptism-gender">
                            <option value="">Seleziona...</option>
                            <option value="F">Femminuccia</option>
                            <option value="M">Maschietto</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Descrivi cosa cerchi *</Label>
                        <Textarea placeholder="Racconta la tua idea per il battesimo... Stile, atmosfera, dettagli che vorresti." value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className="mt-1.5 min-h-[100px]" required data-testid="baptism-description" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Cosa cerchi?</Label>
                          <select value={formData.product_category} onChange={(e) => handleInputChange('product_category', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="baptism-product">
                            <option value="">Seleziona...</option>
                            {PRODUCT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tema</Label>
                          <select value={formData.theme} onChange={(e) => handleInputChange('theme', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="baptism-theme">
                            <option value="">Seleziona...</option>
                            {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Colori</Label>
                          <select value={formData.color_scheme} onChange={(e) => handleInputChange('color_scheme', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="baptism-colors">
                            <option value="">Seleziona...</option>
                            {COLOR_SCHEMES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Invitati</Label>
                          <select value={formData.guest_count} onChange={(e) => handleInputChange('guest_count', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="baptism-guests">
                            <option value="">Seleziona...</option>
                            {GUEST_COUNTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select value={formData.budget} onChange={(e) => handleInputChange('budget', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="baptism-budget">
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Quando?</Label>
                          <select value={formData.timeline} onChange={(e) => handleInputChange('timeline', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="baptism-timeline">
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Foto ispirazione</Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-14 h-14 object-cover rounded-lg" />
                                  <button type="button" onClick={() => removeAttachment(idx)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-sky-300">
                              <input type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" disabled={uploadingFile} />
                              {uploadingFile ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
                              <span className="text-sm text-slate-500">Carica ({formData.attachments.length}/5)</span>
                            </label>
                          )}
                        </div>
                      </div>
                      <Button type="submit" disabled={loading} className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700" data-testid="baptism-submit">
                        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Baby className="w-5 h-5 mr-2" />}
                        Ricevi proposte gratis
                      </Button>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ESEMPI */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Ecco cosa creano i nostri artigiani</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {BAPTISM_EXAMPLES.map((ex, idx) => (
                <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img src={ex.image} alt={ex.result} className="w-full h-48 object-cover" />
                  <div className="p-5">
                    <span className="text-xs font-medium text-sky-600 uppercase">{ex.category}</span>
                    <p className="text-sm text-slate-500 mt-2">"{ex.idea}"</p>
                    <p className="font-semibold text-slate-800 mt-1">→ {ex.result}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-sky-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Cosa dicono i genitori</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {TESTIMONIALS.map((t, idx) => (
                <Card key={idx} className="p-6 bg-white">
                  <div className="flex gap-1 mb-4">{[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-sky-500 text-sky-500" />)}</div>
                  <p className="text-slate-600 italic mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center font-semibold text-sky-700">{t.author[0]}</div>
                    <div><p className="font-medium">{t.author}</p><p className="text-slate-500 text-xs">{t.location}</p></div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-12">Domande frequenti</h2>
            <div className="space-y-4">
              {FAQ_ITEMS.map((faq, idx) => (
                <div key={idx} className="border rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-sky-50">
                    <span className="font-medium">{faq.question}</span>
                    <ChevronRight className={`w-5 h-5 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedFaq === idx && <div className="px-5 pb-5 text-slate-600">{faq.answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-b from-sky-100 to-sky-50">
          <div className="container mx-auto px-4 text-center">
            <Baby className="w-16 h-16 text-sky-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Pronto a organizzare il battesimo perfetto?</h2>
            <p className="text-xl text-slate-600 mb-8">Gli artigiani italiani aspettano la tua richiesta.</p>
            <Button onClick={scrollToForm} size="lg" className="py-6 px-10 text-lg bg-gradient-to-r from-sky-500 to-blue-600" data-testid="baptism-cta-final">
              <Baby className="w-5 h-5 mr-2" /> Ricevi proposte gratis
            </Button>
          </div>
        </section>
      </div>

      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">Per inviare la richiesta devi accedere.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowAuthPrompt(false)} className="flex-1">Annulla</Button>
              <Button onClick={() => navigate('/login?redirect=/battesimi-su-misura')} className="flex-1 bg-sky-500">Accedi</Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
